import { arrayOf } from 'normalizr'

import {
  dealSchema,
  eventTypeSchema,
  partnerSettingsSchema,
  performanceSchema,
  setupformSchema,
  venueSchema,
  eventGroupSchema,
  sellableItemSchema,
  sellableItemGroupSchema,
  promoCodeSchema,
} from 'services/schemas'
import { toISOString } from 'utils/datetime'
import { stringify } from 'utils/urls'
import { deleteSchema, fetchSchema } from './fetch-schema'
import fetch from './fetch'

export default {
  // Performance
  fetchPerformance: performanceId =>
    fetchSchema(`performances/${performanceId}/`, performanceSchema),

  fetchPerformanceTicketDetails: (performanceId, version = 0) =>
    fetchSchema(`performances/${performanceId}/ticket_details/?version=${version}`, performanceSchema),

  acknowledgePerformance: (performanceId, print) =>
    fetch(`performances/${performanceId}/acknowledge/`, 'post', print ? { print: 1 } : null).catch(error => error),

  dealsBatchUpdate: draftDeals =>
    fetchSchema(
      'deals/batch_update/',
      { deals: arrayOf(dealSchema) },
      {
        method: 'post',
        data: { deals: Object.values(draftDeals) },
      },
    ),

  // Settings
  fetchSettings: () =>
    fetch('settings/').catch(error => error),

  fetchPartnerSettings: partnerId =>
    fetchSchema(`partners/${partnerId}/settings/`, partnerSettingsSchema),

  fetchPortalUsers: query =>
    fetch(`portalusers/?q=${query}`).catch(error => error),

  fetchPartners: query =>
    fetch(`partners/?q=${query}`).catch(error => error),

  // Setupforms
  fetchEventTypes: () =>
    fetchSchema('eventtypes/', arrayOf(eventTypeSchema)),

  fetchVenue: (partnerId, venueId) =>
    fetchSchema(`partners/${partnerId}/venues/${venueId}/`, venueSchema),

  fetchSetupform: (setupformId, partnerId) =>
    fetchSchema(`partners/${partnerId}/setupforms/${setupformId}/`, setupformSchema),

  updateSetupform: (setupformId, partnerId, values) =>
    fetchSchema(
      `partners/${partnerId}/setupforms/${setupformId}/`,
      setupformSchema,
      {
        method: 'patch',
        data: {
          id: setupformId,
          ...values,
        },
      }
    ),

  submitSetupform: (setupformId, partnerId) =>
    fetch(`partners/${partnerId}/setupforms/${setupformId}/submit/`, 'post', null, false).catch(error => error),

  deleteSetupform: (setupformId, partnerId) =>
    deleteSchema(`partners/${partnerId}/setupforms/${setupformId}/`, setupformSchema, setupformId),

  bulkUpdateSetupformInstanceGroups: (setupformId, partnerId, actions) =>
    fetch(
      `partners/${partnerId}/setupforms/${setupformId}/instance_groups/bulk_update/`,
      'post',
      actions,
      true
    ).catch(error => error),

  fetchVenueSearchResults: (partnerId, query) =>
    fetchSchema(`partners/${partnerId}/venues/?q=${query}&limit=5`, { results: arrayOf(venueSchema) }),

  uploadImage: (partnerId, setupformId, data) =>
    fetch(`partners/${partnerId}/setupforms/${setupformId}/images/`, 'post', data, false).catch(error => error),

  deleteImage: (partnerId, setupformId, imageId) =>
    fetch(`partners/${partnerId}/setupforms/${setupformId}/images/${imageId}/`, 'delete').catch(error => error),

  loadImage: (partnerId, setupformId) =>
    fetch(`partners/${partnerId}/setupforms/${setupformId}/images/`).catch(error => error),

  createNewVenue: (partnerId, setupformId, data) =>
    fetch(`partners/${partnerId}/setupforms/${setupformId}/new_venue/`, 'post', data, true, false),

  // Home
  fetchEventGroups: partnerId =>
    fetchSchema(`partners/${partnerId}/eventgroups/`, arrayOf(eventGroupSchema)),

  fetchPastEventGroups: (partnerId, url, maxLastStartTime) =>
    fetchSchema(
      url || `partners/${partnerId}/eventgroups/?max_last_start_time=${toISOString(maxLastStartTime)}`,
      { results: arrayOf(eventGroupSchema) }
    ),

  fetchSetupforms: partnerId =>
    fetchSchema(`partners/${partnerId}/setupforms/`, arrayOf(setupformSchema)),

  // Event Group
  fetchEventGroup: eventGroupId =>
    fetchSchema(`eventgroups/${eventGroupId}/`, eventGroupSchema),

  updateEventGroup: (eventGroupId, values) =>
    fetchSchema(
      `eventgroups/${eventGroupId}/`,
      eventGroupSchema,
      {
        method: 'patch',
        data: values
      }
    ),

  createSellableItem: (sellableItemId, eventGroupId, values) =>
    fetchSchema(
      `eventgroups/${eventGroupId}/sellable_items/`,
      sellableItemSchema,
      {
        method: 'post',
        data: values,
      }
    ),

  updateSellableItem: (sellableItemId, values) =>
    fetchSchema(
      `sellable_items/${sellableItemId}/`,
      sellableItemSchema,
      {
        method: 'patch',
        data: values,
      }
    ),

  deleteSellableItem: sellableItemId =>
    deleteSchema(`sellable_items/${sellableItemId}/`, sellableItemSchema, sellableItemId),

  fetchEventGroupPerformances: (eventGroupId, url, { minStartTime, maxStartTime, reverseStartTimeOrder } = {}) => {
    const query = stringify({
      min_start_time: minStartTime && toISOString(minStartTime),
      max_start_time: maxStartTime && toISOString(maxStartTime),
      ordering: reverseStartTimeOrder ? '-start_time' : undefined,
    })
    return fetchSchema(
      url || `eventgroups/${eventGroupId}/performances/${query}`,
      { results: arrayOf(performanceSchema) }
    )
  },

  fetchEventGroupDeals: eventGroupId =>
    fetchSchema(
      `eventgroups/${eventGroupId}/deals/`,
      eventGroupSchema,
      {
        responseModifier: resp => ({ id: eventGroupId, deals: resp }),
      }
    ),

  createDeal: (eventGroupId, values) =>
    fetchSchema(
      `eventgroups/${eventGroupId}/deals/`,
      dealSchema,
      {
        method: 'post',
        data: values,
      }
    ),

  deleteDeal: dealId => deleteSchema(`deals/${dealId}/`, dealSchema, dealId),

  fetchEventGroupSellableItems: eventGroupId =>
    fetchSchema(
      `eventgroups/${eventGroupId}/sellable_items/`,
      eventGroupSchema,
      {
        responseModifier: resp => ({ id: eventGroupId, sellableItems: resp }),
      }
    ),

  fetchEventGroupSellableItemGroups: eventGroupId =>
    fetchSchema(`eventgroups/${eventGroupId}/sellable_item_groups/`, arrayOf(sellableItemGroupSchema)),

  // Signup
  signup: data =>
    fetch('signup/', 'post', data).catch(error => error),

  // Promo codes
  fetchPromoCodesPartnerCodes: partnerId =>
    fetchSchema(`partners/${partnerId}/promocodes/`, arrayOf(promoCodeSchema)),

  createPromoCode: (partnerId, values) =>
    fetchSchema(`partners/${partnerId}/promocodes/`, promoCodeSchema, { method: 'post', data: values }),

  updatePromoCode: (promoCodeId, values) =>
    fetchSchema(`promocodes/${promoCodeId}/`, promoCodeSchema, { method: 'patch', data: values }),
}



/** WEBPACK FOOTER **
 ** ./src/services/api/api.js
 **/